import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { ActivityEvent, Comparison, ComparisonStatus } from "@/api/generated/client/models";
import { Loader2, Activity as ActivityIcon, TrendingUp } from "lucide-react";
import { useState, useMemo } from "react";
import { format } from "date-fns";
import { ComparisonProgressCard } from "./ComparisonProgressCard";
import { AppraisalResponseActions } from "./AppraisalResponseActions";

interface ClaimActivityFeedProps {
    activities: ActivityEvent[];
    comparisons?: Comparison[];
    isLoading: boolean;
}

type FeedItem =
    | { type: 'activity'; data: ActivityEvent; date: Date }
    | { type: 'comparison'; data: Comparison; date: Date };

// TODO this will have many more types in the future, for now we only have activities and comparisons. We will break this out later.
export function ClaimActivityFeed({ activities, comparisons = [], isLoading }: ClaimActivityFeedProps) {
    const [activitySearch, setActivitySearch] = useState("");
    const [activityFilter, setActivityFilter] = useState<string | "all">("all");

    const feedItems = useMemo(() => {
        const items: FeedItem[] = [];

        // Add activities (exclude note_created since notes have their own tab)
        activities.forEach(a => {
            if ((a.type as string) === 'note_created') return;
            items.push({ type: 'activity', data: a, date: new Date(a.createdAt) });
        });

        // Add completed or failed comparisons
        comparisons.forEach(c => {
            const isCompleted = c.status === ComparisonStatus.Completed || c.status === 'completed' as any;
            const isFailed = c.status === ComparisonStatus.Failed || c.status === 'failed' as any;

            if (isCompleted || isFailed) {
                // Use updatedAt as the completion timestamp (when the comparison actually finished)
                items.push({ type: 'comparison', data: c, date: new Date(c.updatedAt) });
            }
        });

        // Sort descending (newest first)
        return items.sort((a, b) => b.date.getTime() - a.date.getTime());
    }, [activities, comparisons]);

    const filteredItems = feedItems.filter(item => {
        if (item.type === 'comparison') {
            // Show comparisons if filter is 'all' or 'comparisons'
            if (activityFilter !== "all" && activityFilter !== "comparisons") return false;
            if (activitySearch) return false; // Basic search skip for now
            return true;
        }

        const activity = item.data as ActivityEvent;
        const eventType = activity.type as string;
        
        // Apply type filter
        let matchesFilter = true;
        if (activityFilter === "claim") {
                 matchesFilter = eventType === 'claim_created' || eventType === 'claim_updated' || 
                     eventType === 'status_changed' || eventType === 'appraisal_requested' || eventType === 'appraisal_responded' || eventType === 'claim_assigned' || 
                   eventType === 'claim_unassigned';
        } else if (activityFilter === "comparisons") {
            matchesFilter = eventType?.startsWith('comparison_') ?? false;
        } else if (activityFilter === "documents") {
            matchesFilter = eventType === 'document_uploaded' || eventType === 'file_uploaded';
        } else if (activityFilter === "forms") {
            matchesFilter = eventType === 'form_sent' || eventType === 'form_completed' || eventType === 'form_cancelled';
        } else if (activityFilter === "emails") {
            matchesFilter = eventType === 'email_sent' || eventType === 'email_received';
        } else if (activityFilter === "tasks") {
            matchesFilter = eventType?.startsWith('task_') ?? false;
        } else if (activityFilter === "supplements") {
            matchesFilter = eventType?.startsWith('supplement_') ?? false;
        } else if (activityFilter !== "all") {
            matchesFilter = false;
        }

        if (!matchesFilter) return false;

        // Apply search filter
        if (activitySearch && !activity.message.toLowerCase().includes(activitySearch.toLowerCase())) return false;
        return true;
    });

    return (
        <ScrollArea className="h-full w-full">
            <div className="p-6 overflow-x-hidden">
                <div className="mb-6">
                    <div className="mb-4">
                        <div>
                            <h2 className="text-xl font-semibold">Activity Timeline</h2>
                            <p className="text-sm text-muted-foreground mt-1">Track all changes and updates</p>
                        </div>
                    </div>

                    {(() => {
                        const processingComparison = comparisons?.find(c => 
                            c.status === ComparisonStatus.Processing || c.status === 'processing' as any || c.status === 'pending' as any
                        );
                        return processingComparison ? (
                            <div className="mb-4">
                                <ComparisonProgressCard
                                    comparisonId={processingComparison.id}
                                    initialData={processingComparison}
                                />
                            </div>
                        ) : null;
                    })()}

                    <div className="space-y-3">
                        <Input
                            placeholder="Search activities..."
                            value={activitySearch}
                            onChange={(e) => setActivitySearch(e.target.value)}
                        />
                        <div className="overflow-x-auto pb-1">
                            <div className="flex items-center bg-muted/50 border rounded-lg p-0.5 gap-0 w-full">
                                {[
                                    { key: "all", label: "All" },
                                    { key: "claim", label: "Claim" },
                                    { key: "comparisons", label: "Comparisons" },
                                    { key: "documents", label: "Documents" },
                                    { key: "emails", label: "Emails" },
                                    { key: "forms", label: "Forms" },
                                    { key: "tasks", label: "Tasks" },
                                    { key: "supplements", label: "Supplements" },
                                ].map(({ key, label }) => (
                                    <button
                                        key={key}
                                        onClick={() => setActivityFilter(key)}
                                        className={`flex-1 py-1 px-2 rounded-md font-medium text-[11px] whitespace-nowrap transition-all text-center ${
                                            activityFilter === key
                                                ? "bg-primary/10 text-primary shadow-sm border border-primary/20"
                                                : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>
                ) : filteredItems.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">No activities found</div>
                ) : (
                    <div className="space-y-6">
                        {filteredItems.map((item, idx) => {
                            if (item.type === 'comparison') {
                                const comparison = item.data as Comparison;
                                return (
                                    <div key={`comp-${comparison.id}`} className="flex gap-4 w-full max-w-full min-w-0">
                                        <div className="relative">
                                            <div className="h-10 w-10 bg-blue-100 border border-blue-200 shadow-xs rounded-full flex items-center justify-center z-10">
                                                <TrendingUp className="h-5 w-5 text-blue-600" />
                                            </div>
                                            {idx < filteredItems.length - 1 && (
                                                <div className="absolute top-10 left-5 w-px h-full bg-border -translate-x-1/2" />
                                            )}
                                        </div>
                                        <div className="flex-1 pb-4 min-w-0 overflow-hidden">
                                            <div className="mb-1 text-xs text-muted-foreground text-right">
                                                {format(item.date, "MMM d, h:mm a")}
                                            </div>
                                            <ComparisonProgressCard
                                                comparisonId={comparison.id}
                                                initialData={comparison}
                                                className="mb-0 border shadow-sm w-full"
                                            />
                                        </div>
                                    </div>
                                );
                            }

                            const activity = item.data as ActivityEvent;
                            return (
                                <div key={`act-${activity.id}`} className="flex gap-4 w-full max-w-full min-w-0">
                                    <div className="relative">
                                        <div className="h-10 w-10 bg-background border shadow-xs rounded-full flex items-center justify-center z-10">
                                            <ActivityIcon className="h-4 w-4 text-gray-400" />
                                        </div>
                                        {idx < filteredItems.length - 1 && (
                                            <div className="absolute top-10 left-5 w-px h-full bg-border -translate-x-1/2" />
                                        )}
                                    </div>
                                    <div className="flex-1 pb-4 min-w-0 overflow-hidden">
                                        <div className="bg-card border rounded-lg p-4 shadow-sm w-full">
                                            <div className="flex justify-between gap-2 flex-wrap">
                                                <p className="text-sm font-medium break-words flex-1 min-w-0" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{activity.message}</p>
                                                <span className="text-xs text-muted-foreground flex-shrink-0">{format(new Date(activity.createdAt), "MMM d, h:mm a")}</span>
                                            </div>
                                            {activity.actorName && (
                                                <p className="text-xs text-muted-foreground mt-1">by {activity.actorName}</p>
                                            )}
                                            {activity.type === 'appraisal_responded' && typeof activity.metadata?.notes === 'string' && activity.metadata.notes.trim() && (
                                                <div className="mt-3 rounded-md bg-muted/50 p-3">
                                                    <p className="text-xs font-medium text-muted-foreground">Review notes</p>
                                                    <p className="mt-1 whitespace-pre-wrap break-words text-sm">{activity.metadata.notes}</p>
                                                </div>
                                            )}
                                            {activity.type === 'appraisal_requested' && activity.claimId && !activities.some(
                                                (response) => response.type === 'appraisal_responded'
                                                    && response.claimId === activity.claimId
                                                    && String(response.metadata?.requestEventId) === String(activity.id),
                                            ) && (
                                                <AppraisalResponseActions claimId={activity.claimId} requestEventId={activity.id} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </ScrollArea>
    );
}
